/*
 * Copyright (c) 2016-2018. campoint AG.
 * This file is licensed under the MIT License (https://opensource.org/licenses/MIT).
 */

import { supportsWebSockets } from './capabilities';
import { logger } from './logging/logger';
import { Provider } from './provider';

function dataUrlToBlob(dataUrl: string): Blob {
    let raw: BlobPart;
    let contentType: string;

    if (!dataUrl.includes(';base64,')) {
        const parts = dataUrl.split(',');
        contentType = parts[0].split(':')[1];
        raw = parts[1];
    } else {
        const parts = dataUrl.split(';base64,');
        contentType = parts[0].split(':')[1];
        const decoded = window.atob(parts[1]);
        const rawLength = decoded.length;
        raw = new Uint8Array(rawLength);
        for (let i = 0; i < rawLength; ++i) {
            raw[i] = decoded.charCodeAt(i);
        }
    }

    return new Blob([raw], { type: contentType });
}

export class PicUpStream {
    private _maxFps: number;
    private _target: string;

    private _provider: Provider;
    private _socket: WebSocket;

    private _publishLoop: ReturnType<typeof setTimeout>;
    private _publishStopRequested = false;

    constructor(provider: Provider) {
        this._provider = provider;
    }

    public publish(target: string, targetFps = 1): void {
        this._maxFps = Math.max(1, targetFps);
        this._target = target;

        if (supportsWebSockets) {
            this._publishWS();
        }
    }

    public stop(): void {
        this._stopPublishLoop();

        if (this._socket) {
            this._socket.close();
            this._socket = undefined;
        }
    }

    private _stopPublishLoop(): void {
        if (this._publishLoop !== undefined) {
            this._publishStopRequested = true;
            clearTimeout(this._publishLoop);
            this._publishLoop = undefined;
        }
    }

    private _publishWS(): void {
        const wsSource = this._target.replace(/https?:/, 'wss:').replace(/:\d+\//, ':443/');
        const socket = new WebSocket(wsSource);
        socket.onopen = (): void => {
            const command = `{"action":"CMDS_STARTVIDEOPUSH","params":{"fps":${this._maxFps},"base64":true}}`;
            socket.send(command);
            this._startPublishLoop();
        };
        socket.onmessage = (): void => {
            // ignore
        };
        socket.onerror = (event): void => {
            logger.warn(event);
            this.stop();
            // TODO
        };
        socket.onclose = (event): void => {
            logger.log('PicUpStream.socket.onclose', event);
            this._stopPublishLoop();
        };
        this._socket = socket;
    }

    private _startPublishLoop(): void {
        this._publishStopRequested = false;

        this._publishLoopCall();
    }

    private async _publishLoopCall(): Promise<void> {
        const snapshot = await this._provider.getSnapshot();
        if (snapshot) {
            this._send(snapshot);
        }

        if (!this._publishStopRequested) {
            this._publishLoop = setTimeout(this._publishLoopCall.bind(this), 1000 / this._maxFps);
        }
    }

    private _send(snapshot: string | ArrayBuffer | Blob): void {
        if (this._socket.OPEN && this._socket.bufferedAmount < 32768) {
            if (typeof snapshot === 'string') {
                this._socket.send(dataUrlToBlob(snapshot));
            } else {
                this._socket.send(snapshot);
            }
        }
    }
}
